import { Checkbox } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import React, { useState } from 'react';

export interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
  textAlign?: 'left' | 'center' | 'right';
  minWidth?: number;
  maxWidth?: number;
  tooltip?: string;
  disableRequestSort?: boolean;
}

interface CustomTableHeadProps {
  columns: HeadCell[];
  order: 'asc' | 'desc';
  orderBy: string;
  hasSelection?: boolean;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  handleSelectAll?: (isSelectAll: boolean) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
  }),
);

const CustomTableHead: React.FC<CustomTableHeadProps> = ({
  order,
  orderBy,
  columns,
  hasSelection = false,
  onRequestSort,
  handleSelectAll,
}) => {
  const classes = useStyles();
  const [isSelectedAll, setIsSelectedAll] = useState(false);

  const createSortHandler = (property: string, disableRequestSort = false) => (
    event: React.MouseEvent<unknown>,
  ) => {
    !disableRequestSort && onRequestSort(event, property);
  };

  const handleSelect = () => {
    if (handleSelectAll) {
      setIsSelectedAll(!isSelectedAll);
      handleSelectAll(!isSelectedAll);
    }
  };

  return (
    <TableHead style={{ backgroundColor: 'transparent' }}>
      <TableRow>
        {hasSelection && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              checked={isSelectedAll}
              onClick={handleSelect}
            />
          </TableCell>
        )}
        {columns.map(column => (
          <TableCell
            style={{ backgroundColor: 'white' }}
            key={column.id}
            align={column.textAlign || column.numeric ? 'right' : 'center'}
            padding={column.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === column.id ? order : false}
          >
            <TableSortLabel
              hideSortIcon
              disabled={column.disableRequestSort}
              active={orderBy === column.id}
              direction={orderBy === column.id ? order : 'desc'}
              onClick={createSortHandler(column.id, column.disableRequestSort)}
            >
              <Tooltip
                enterDelay={700}
                enterNextDelay={1500}
                title={column.tooltip || ''}
                placement="bottom"
                arrow
              >
                <Typography
                  color="textPrimary"
                  style={{ fontWeight: 'bolder' }}
                  variant="subtitle1"
                >
                  {column.label}{' '}
                  {column.tooltip && <InfoOutlinedIcon color="disabled" />}
                </Typography>
              </Tooltip>
              {orderBy === column.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default CustomTableHead;
